import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { LanguageService } from '@fcom/ui-translate';
import { RootPaths } from '@fcom/core/constants';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';
import { ButtonSize, ButtonTheme, ButtonType } from '@fcom/ui-components/components/buttons/button/enums';
import { IconButtonSize, IconButtonTheme } from '@fcom/ui-components/components/buttons/icon-button/enums';
import { IconPosition } from '@fcom/ui-components/components/icons/enums';

@Component({
  selector: 'fin-site-search-header-widget-starter',
  templateUrl: 'site-search-header-widget-starter.component.html',
  styleUrls: ['site-search-header-widget-starter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SiteSearchHeaderWidgetStarterComponent implements OnInit {
  private languageService = inject(LanguageService);
  private formBuilder = inject(UntypedFormBuilder);
  private router = inject(Router);
  private themeService = inject(FinnairThemeService);

  isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly IconPosition = IconPosition;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonSize = ButtonSize;
  readonly ButtonType = ButtonType;
  readonly IconButtonTheme = IconButtonTheme;
  readonly IconButtonSize = IconButtonSize;

  headerQueryField = viewChild<ElementRef>('headerQueryField');

  expandable = input(false);

  enableNewLanguageSelector = input(false);

  searchButtonIconOnly = input(false);

  context = input<string>();

  searchGtmEvent = output<void>();

  query = '';
  siteSearchPath = '';
  searchForm: UntypedFormGroup;
  inputExpanded = signal(false);

  constructor() {
    this.siteSearchPath = `/${[this.languageService.langValue, RootPaths.SITE_SEARCH_ROOT].filter(Boolean).join('/')}`;
  }

  ngOnInit(): void {
    this.searchForm = this.formBuilder.group({
      searchKey: [],
    });
    this.inputExpanded.set(!this.expandable());
  }

  expandSearch(): void {
    this.inputExpanded.set(true);
    this.headerQueryField().nativeElement.focus();
    this.searchGtmEvent.emit();
  }

  collapseSearch(): void {
    this.inputExpanded.set(false);
  }

  onSubmit(): void {
    const q = this.searchForm.get('searchKey').value;
    this.triggerSearch(q);
  }

  onBlur(): void {
    if (this.expandable() && !this.searchForm.get('searchKey').value) {
      this.collapseSearch();
    }
  }

  triggerSearch(query: string): void {
    const q = query ? `?query=${query}` : '';
    this.router.navigateByUrl(`${this.languageService.langValue}/search${q}`);

    this.collapseSearch();
    this.searchForm.reset(this.searchForm.get('searchKey'));
  }
}
